import React, { useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { SegmentsSection } from 'views/Offers/components/sections';
import { IOfferDesignOption } from 'views/Offers/hooks/useOfferDesignOptions';
import {
  getPriorityOptions,
  PRIORITY_NONE_VALUE
} from 'views/Offers/Popups/Form/PopupsForm.consts';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { RewardCalendarFormFields } from '../../RewardCalendarForm.consts';

interface ISettingsTabProps {
  offerDesignOptions: IOfferDesignOption[];
  isNameTaken: boolean;
  isPublisherOfferIdTaken: boolean;
}

const SettingsTab: React.FC<ISettingsTabProps> = ({
  offerDesignOptions,
  isNameTaken,
  isPublisherOfferIdTaken
}): React.ReactElement => {
  const form = useFormContext<RewardCalendarFormFields>();
  const priorityOptions = useMemo(() => getPriorityOptions(), []);

  const nameMessage = useMemo((): React.ReactNode => {
    if (isNameTaken) {
      return <FormMessage>Name already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isNameTaken]);

  const externalIdMessage = useMemo((): React.ReactNode => {
    if (isPublisherOfferIdTaken) {
      return <FormMessage>External ID already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isPublisherOfferIdTaken]);

  const renderNameField = useCallback(
    ({ field }: { field: any }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <Input isRequired label="Name" placeholder="Name" {...field} />
        </FormControl>
        {nameMessage}
      </FormItem>
    ),
    [nameMessage]
  );

  const renderExternalIdField = useCallback(
    ({ field }: { field: any }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <Input
            isRequired
            label="External ID"
            placeholder="External ID"
            {...field}
          />
        </FormControl>
        {externalIdMessage}
      </FormItem>
    ),
    [externalIdMessage]
  );

  const handleOfferDesignChange = useCallback(
    (value: string): void => {
      if (value === form.getValues('offerUiId')) return;
      form.setValue('offerUiId', value, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const renderOfferDesignField = useCallback(
    ({
      field: { value }
    }: {
      field: { value: string };
    }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <OfferDesignSelector
            options={offerDesignOptions}
            selectedValue={value}
            onChange={handleOfferDesignChange}
            listDataTestId="rewardCalendarOfferDesignList"
            placeholder="Select Offer Design"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    ),
    [offerDesignOptions, handleOfferDesignChange]
  );

  const renderPriorityField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: { value: number; onChange: (...event: unknown[]) => void };
    }): React.ReactElement => {
      const selectedPriority = priorityOptions.find(
        (option) => option.value === value
      );
      const handlePrioritySelect = (raw: string): void => {
        const next = Number(raw);
        if (Number.isNaN(next) || next === value) return;
        onChange(next);
      };
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={String(value)}
              label="Priority"
              placeholder="Enter Number"
              isRequired
              defaultSelected={selectedPriority}
              options={priorityOptions.filter(
                (option) => option.value !== PRIORITY_NONE_VALUE
              )}
              onOptionChange={handlePrioritySelect}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    },
    [priorityOptions]
  );

  const handleSegmentsChange = useCallback(
    (segments: string[]): void => {
      const prev = form.getValues('segments') ?? [];
      if (
        prev.length === segments.length &&
        prev.every((s: string, i: number): boolean => s === segments[i])
      ) {
        return;
      }
      form.setValue('segments', segments, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Complete the required fields for your offer."
        className="mb-5"
      />
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-5">
          <FormField
            control={form.control}
            name="name"
            render={renderNameField}
          />
          <FormField
            control={form.control}
            name="publisherOfferId"
            render={renderExternalIdField}
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <FormField
            control={form.control}
            name="priority"
            render={renderPriorityField}
          />
          <FormField
            control={form.control}
            name="offerUiId"
            render={renderOfferDesignField}
          />
        </div>
      </div>
      <Separator className="mt-5 mb-9 bg-base-border" />
      <SegmentsSection
        items={form.watch('segments') || []}
        onItemsChange={handleSegmentsChange}
      />
    </div>
  );
};

export default SettingsTab;
